import React, { useState } from 'react';
import { Table, Modal } from 'antd';
import { Box, Flex, IconButton, Input, Text } from '@chakra-ui/react';
import './EntityTable.scss';
import { delEntity } from '../../slices/masterData/entityManagerSlice';
import { ReactComponent as DeleteIcon } from '../../assets/delete.svg';
import { useDispatch } from 'react-redux';
import { ReactComponent as ChevronDown } from '../../assets/chevron-down.svg';
import { FaSearch } from 'react-icons/fa';
import { get, isEmpty } from 'lodash';
import { InputGroup } from '../ui/input-group';
import { Tag } from '@/components/ui/tag';
import { PopoverRoot, PopoverContent, PopoverBody, PopoverTrigger, PopoverArrow } from '@/components/ui/popover';
import { getTenantFromDomain } from '../../utils';

const { Column } = Table;
const tenant = getTenantFromDomain();

const renderMultiSelectTag = (name, systemId) => {
  const vowelRegex = '[aieouAIEOU]$';
  const getTagStyles = () => {
    if ((systemId && isNaN(systemId) && systemId.match(vowelRegex)) || systemId % 2 === 0) {
      return {
        bgColor: '#FBD38D',
        color: '#000000',
      };
    }
    return {
      bgColor: '#805AD5',
      color: '#FFFFFF',
    };
  };
  return (
    <Tag key={name} variant="solid" margin="3px" fontSize="11px" fontWeight="medium" {...getTagStyles()}>
      {name}
    </Tag>
  );
};

const renderSortIcon = order => {
  if (order === 'ascend') {
    return (
      <Box transform="rotate(180deg)" marginLeft="10px">
        <ChevronDown />
      </Box>
    );
  }
  if (order === 'descend') {
    return (
      <Box>
        <ChevronDown />
      </Box>
    );
  }
  return null;
};

const lookupRenderer = (
  entityDefinition,
  fieldDef,
  referencedEntities,
  setActiveEntityid,
  setShowEditModel,
  dispatch,
  setMetaModal
) => {
  const { dataType, referenceType } = fieldDef;
  let renderFn = data => data;

  if (['float', 'number', 'integer'].includes(dataType)) {
    renderFn = data => <Box width="100%">{data}</Box>;
  }

  if (['boolean'].includes(dataType)) {
    renderFn = data => <Text as="p"> {data ? 'Yes' : 'No'}</Text>;
  }

  if (['longtext'].includes(dataType)) {
    const limit = 50;
    renderFn = str => (str && str.length > limit ? `${str.substr(0, limit - 1)}…` : str);
  }

  // Special renderer for MDM Activity Logs meta column: open JSON in modal
  if (fieldDef.name === 'meta') {
    renderFn = data => {
      if (!data) return '-';
      const handleOpen = () => {
        let content = data;
        if (typeof data === 'string') {
          try {
            content = JSON.parse(data);
          } catch (e) {
            content = data;
          }
        }
        setMetaModal({ open: true, content });
      };
      return (
        <a onClick={e => { e.preventDefault(); handleOpen(); }} href="javascript:void(0);">
          View
        </a>
      );
    };
  }

  // Special renderer for MDM Activity Logs edit_date: format YYYY-MM-DD hh:mm AM/PM
  if (entityDefinition?.name === 'mdm-activity-logs' && fieldDef.name === 'edit_date') {
    renderFn = value => {
      if (!value) return '-';
      const d = new Date(value);
      if (isNaN(d.getTime())) return String(value);
      const pad = n => String(n).padStart(2, '0');
      const y = d.getFullYear();
      const m = pad(d.getMonth() + 1);
      const day = pad(d.getDate());
      let h = d.getHours();
      const ampm = h >= 12 ? 'PM' : 'AM';
      h = h % 12;
      if (h === 0) h = 12;
      const min = pad(d.getMinutes());
      const formatted = `${y}-${m}-${day} ${pad(h)}:${min} ${ampm}`;
      return <Box width="100%">{formatted}</Box>;
    };
  }

  if (dataType === 'reference' && referenceType === 'nested') {
    renderFn = data => {
      const processedValue = data
        ? Object.values(data).reduce((prev, current) => {
            if (current && current.length) {
              prev += ` ${current}`;
            }
            return prev;
          }, '')
        : '';
      return <Box>{processedValue}</Box>;
    };
  }

  if (dataType === 'reference' && ['multi-select', 'select'].includes(referenceType)) {
    renderFn = data => {
      const refEnt = referencedEntities[fieldDef.referenceEntityType];
      if (!refEnt) {
        return null;
      }
      let refEntId = refEnt.entityDefinition.systemIdField;
      let refEntDisplayId = refEnt.entityDefinition.displayIdField;
      let { entities } = refEnt;
      if (fieldDef.referenceEntitySubType) {
        // For form fileds reference, we have to look into sub entity type.
        entities = entities.filter(ent => ent.type === fieldDef.referenceEntitySubType);
        refEntId = 'value';
        refEntDisplayId = 'value';
      }
      const match = entities.filter(ent => {
        const value = refEntId ? ent[refEntId] : ent;
        if (Array.isArray(data)) {
          return data.includes(value);
        }
        return value === data;
      });

      let processedValue;

      if (match && match.length) {
        if (match.length > 1) {
          processedValue = (
            <Box height="100px" width="200px" overflowY="auto" className="scrollbar-style">
              {match
                .map(value =>
                  value[refEntDisplayId]
                    ? {
                        name: value[refEntDisplayId],
                        systemId: value[refEnt.entityDefinition.systemIdField],
                      }
                    : { name: value, systemId: value[refEnt.entityDefinition.systemIdField] }
                )
                .map(({ name, systemId }) => renderMultiSelectTag(name, systemId))}
            </Box>
          );
        } else {
          processedValue = match[0][refEntDisplayId];
        }
      } else {
        processedValue = 'None';
      }
      return <Box>{processedValue}</Box>;
    };
  }
  if (dataType === 'deleteAction') {
    renderFn = (data, record) => {
      return (
        <Flex justifyContent="center">
          <IconButton
            color="actionSecondary.500"
            backgroundColor="inherit"
            onClick={() => {
              // eslint-disable-next-line no-alert
              const confirmation = window.confirm(
                `Are you sure you want to delete this ${entityDefinition.displayName}?`
              );
              if (confirmation) {
                dispatch(delEntity({ entityDef: entityDefinition, entityid: record[entityDefinition.systemIdField] }));
              }
            }}
          >
            <DeleteIcon />
          </IconButton>
        </Flex>
      );
    };
  }

  if (entityDefinition.systemIdField === fieldDef.name) {
    const innerRenderFn = renderFn;
    renderFn = data => {
      return (
        <a
          onClick={e => {
            e.preventDefault();
            setActiveEntityid(data);
            setShowEditModel(true);
          }}
          href="javascript:void(0);"
          style={{ color: tenant === 'cfc' ? '#0F1B48' : ''}}
        >
          {innerRenderFn(data)}
        </a>
      );
    };
  }
  return renderFn;
};

const EntityTable = ({
  entityDefinition,
  entities,
  referencedEntities,
  setActiveEntityid,
  setShowEditModel,
  pageSize,
  currentPage,
}) => {
  const [searchState, setSearchState] = useState({
    searchFilteredEntities: [],
    searchValues: {},
    searchArr: [],
    useSearch: false,
  });

  const dispatch = useDispatch();
  const [metaModal, setMetaModal] = useState({ open: false, content: null });
  const hideFields = f => {
    // Multi-Nested reference entities no need to show in table list, too much details.
    const { dataType, referenceType } = f;
    if (dataType === 'reference' && referenceType === 'multi-nested') {
      return false;
    }
    if (f.hideList) {
      // Insignificant fields no need to be shown in table list.
      return false;
    }
    if (dataType === 'component') {
      // No need to render custom component
      return false;
    }
    return true;
  };

  const findFilterValues = name => {
    const referencedEntity = entityDefinition.fields.find(field => field.name === name);

    const uniqValues = entities.reduce((accumulate, current, i) => {
      accumulate[current[name]] = current[name];
      return accumulate;
    }, {});
    return Object.entries(uniqValues).map(([key, value]) => {
      const entry = get(referencedEntities[referencedEntity.referenceEntityType], 'entities', []).find(entity => {
        return (
          entity[referencedEntities[referencedEntity.referenceEntityType].entityDefinition.systemIdField] === value
        );
      });
      return { text: value ? get(entry, 'name', value) : 'None', value };
    });
  };

  const handleSearch = (e, colName) => {
    let newSearchState = { ...searchState };
    const searchValue = e.target.value;
    let filteredBySearch = entities;
    let useSearch = '';
    const existingSearchIndexOfCol = newSearchState.searchArr.findIndex(item => item.colName === colName);
    if (existingSearchIndexOfCol > -1) {
      newSearchState.searchArr.splice(existingSearchIndexOfCol, 1);
    }
    newSearchState.searchArr = [
      ...newSearchState.searchArr,
      {
        colName,
        searchValue,
      },
    ];
    newSearchState.searchArr.forEach(searchParameter => {
      useSearch += searchParameter.searchValue;
      filteredBySearch = filteredBySearch.filter(data =>
        `${data[searchParameter.colName]}`.toLowerCase().includes(searchParameter.searchValue.toLowerCase())
      );
    });
    newSearchState = {
      ...newSearchState,
      searchFilteredEntities: filteredBySearch,
    };
    setSearchState({ ...newSearchState, useSearch: !isEmpty(useSearch) });
  };

  return (
    <>
      <Table
      pagination={{ pageSize, current: currentPage, style: { display: 'none' } }}
      rowClassName="entity-table-row"
      className="entity-table"
      dataSource={(searchState.useSearch ? searchState.searchFilteredEntities : entities).map((entity, i) => ({
        ...entity,
        key: i,
      }))}
    >
      {[
        ...entityDefinition.fields,
        entityDefinition.deletable && {
          name: 'delete_action',
          displayName: '',
          dataType: 'deleteAction',
        },
      ]
        .filter(hideFields)
        .map(fieldDef => {
          let sorter = null;
          let onFilter = null;
          let filters = null;
          // Adding sorter and filter to table.
          if (['shorttext', 'text'].includes(fieldDef.dataType) || fieldDef.referenceType === 'select') {
            sorter = (x, y) => {
              const a = x[fieldDef.name];
              const b = y[fieldDef.name];
              if (a && b && typeof a === 'string' && typeof b === 'string') {
                return a.localeCompare(b);
              }
              return a - b;
            };
          } else if (['integer', 'number'].includes(fieldDef.dataType)) {
            sorter = (a, b) => a[fieldDef.name] - b[fieldDef.name];
          }
          // It's performance intensive, only do it for filtable field.
          if (fieldDef.filterable) {
            filters = findFilterValues(fieldDef.name);
            onFilter = (value, record) => {
              return record[fieldDef.name] === value;
            };
          }
          return (
            <Column
              title={({ sortColumns }) => {
                const sortedColumn = sortColumns?.find(({ column }) => column.key === fieldDef.name);
                return (
                  <Flex justifyContent="space-between" alignItems="center">
                    <Text>{fieldDef.displayName}</Text>
                    {sortedColumn ? renderSortIcon(sortedColumn.order) : null}
                  </Flex>
                );
              }}
              {...(fieldDef.searchable && {
                filterDropdown: () => <Box> </Box>,
                filterIcon: () => (
                  <PopoverRoot placement="top">
                    <PopoverTrigger asChild>
                      <IconButton
                        variant="unstyled"
                        fontSize="11px"
                        minWidth="11.37px"
                        height="100%"
                        _focus={{
                          outline: 'none',
                        }}
                        color={searchState.searchValues[fieldDef.name] ? 'accent.1' : 'inherit'}
                      >
                        <FaSearch />
                      </IconButton>
                    </PopoverTrigger>
                    <PopoverContent
                      borderColor="neutral.5"
                      borderRadius="8px"
                      height="52px"
                      width="208px"
                      _focus={{
                        outline: 'none',
                      }}
                    >
                      <PopoverArrow borderColor="neutral.5" />
                      <PopoverBody padding="8px 12px" color="neutral.1">
                        <InputGroup endElement={<FaSearch />}>
                          <Input
                            placeholder="Search"
                            border="none"
                            backgroundColor="card.default"
                            _focus={{
                              border: 'none',
                            }}
                            value={searchState.searchArr.find(item => item.colName === fieldDef.name)?.searchValue}
                            onChange={e => handleSearch(e, fieldDef.name)}
                          />
                        </InputGroup>
                      </PopoverBody>
                    </PopoverContent>
                  </PopoverRoot>
                ),
              })}
              dataIndex={fieldDef.name}
              key={fieldDef.name}
              sorter={sorter}
              onFilter={onFilter}
              filters={filters}
              render={lookupRenderer(
                entityDefinition,
                fieldDef,
                referencedEntities,
                setActiveEntityid,
                setShowEditModel,
                dispatch,
                setMetaModal
              )}
            />
          );
        })}
      </Table>
      <Modal
        open={metaModal.open}
        onCancel={() => setMetaModal({ open: false, content: null })}
        footer={null}
        width={800}
        title="Meta"
      >
        <Box maxHeight="70vh" overflowY="auto" className="scrollbar-style">
          <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {(() => {
              const c = metaModal.content;
              if (c == null) return '';
              if (typeof c === 'string') return c;
              try {
                return JSON.stringify(c, null, 2);
              } catch (e) {
                return String(c);
              }
            })()}
          </pre>
        </Box>
      </Modal>
    </>
  );
};

export default EntityTable;
